import { type Component, createEffect, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Button from '~/components/Button'
import Form from '~/components/Form'
import Input from '~/components/Input'
import MaskInput from '~/components/MaskInput'
import Modal from '~/components/Modal'
import Switch from '~/components/Switch'
import { variantLabel } from '~/services/product.service'
import type { DraftVariantType } from '~/types/ProductForm'

export interface VariantModalPropsType {
  isOpen: boolean
  onClose: () => void
  variant: DraftVariantType | null
  /** Tarifa sobre la que se edita el precio (la seleccionada en el listado). */
  tariffName: string
  onSave: (v: { sku: string; minStock: number; active: boolean; overridePrice: string }) => void
}

/** Edición de una variante (SKU, precio de la tarifa actual, mínimo, activa). */
const VariantModal: Component<VariantModalPropsType> = props => {
  const [draft, setDraft] = createStore<{ sku: string; minStock: number; active: boolean; overridePrice: string }>({
    sku: '',
    minStock: 0,
    active: true,
    overridePrice: ''
  })

  createEffect(() => {
    if (!props.isOpen) return
    const v = props.variant
    setDraft(
      v
        ? {
            sku: v.sku,
            minStock: v.minStock,
            active: v.active,
            overridePrice: v.overrides[props.tariffName] != null ? String(v.overrides[props.tariffName]) : ''
          }
        : { sku: '', minStock: 0, active: true, overridePrice: '' }
    )
  })

  const isValid = () => draft.sku.trim().length > 0

  const handleSave = () => {
    if (!isValid()) return
    props.onSave({ sku: draft.sku.trim(), minStock: Number(draft.minStock) || 0, active: draft.active, overridePrice: draft.overridePrice.trim() })
  }

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="sm" closable>
      <Modal.Header>
        <h2 class="text-lg font-bold text-foreground">Editar variante</h2>
      </Modal.Header>
      <Modal.Body>
        <Form id="variant-form" onSubmit={handleSave}>
          <div class="space-y-4">
            <Show when={props.variant}>
              <div>
                <p class="text-xs text-foreground-subtle mb-1">Combinación</p>
                <p class="font-medium text-foreground">{variantLabel(props.variant!)}</p>
              </div>
            </Show>
            <Input label="SKU" value={draft.sku} onInput={e => setDraft('sku', e.currentTarget.value)} placeholder="TALLA-40" required />
            <MaskInput
              label={`Precio ${props.tariffName}`}
              maskType="currency"
              currency="COP"
              locale="es-CO"
              fractionDigits={0}
              min={0}
              value={draft.overridePrice}
              onValueChange={v => setDraft('overridePrice', v)}
              placeholder="Vacío = precio de la tarifa"
              helperText="Vacío significa «el de la tarifa»; se escribe sólo cuando se sale del precio general. Formato moneda COP."
            />
            <Input label="Mínimo" type="number" min="0" value={String(draft.minStock)} onInput={e => setDraft('minStock', Number(e.currentTarget.value) || 0)} />
            <Show when={props.variant?.id != null}>
              <div>
                <p class="text-xs text-foreground-subtle mb-1">Existencias</p>
                <p class="font-medium text-foreground">{props.variant!.stock.toLocaleString('es-CO')}</p>
              </div>
            </Show>
            <Switch label="Activa" checked={draft.active} onChange={e => setDraft('active', e.currentTarget.checked)} />
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <div class="flex justify-end gap-2">
          <Button variant="soft" color="default" onClick={props.onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="variant-form" disabled={!isValid()}>
            Guardar
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default VariantModal
